import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Lock, Mail } from "lucide-react";
import { useTalentAuth } from "../../contexts/TalentAuthContext";
import { useCustomerAuth } from "../../contexts/CustomerAuthContext";
import Header from "../public/header";
import Footer from "../public/Footer";

declare global {
  interface Window {
    google: any;
  }
}


const TalentLogin: React.FC = () => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const googleButtonRef = React.useRef<HTMLDivElement>(null);
  const { login: talentLogin, loginWithGoogle: talentLoginWithGoogle } =
    useTalentAuth();
  const { login: customerLogin, loginWithGoogle: customerLoginWithGoogle } =
    useCustomerAuth();
  const navigate = useNavigate();

  // Handle Google OAuth callback
  const handleGoogleCallback = useCallback(
    async (response: any) => {
      try {
        console.log("Google OAuth: Callback received:", response);

        setIsGoogleLoading(true);
        setMessage({ type: "", text: "" });

        // Try talent login first
        console.log("Google OAuth: Attempting talent login...");
        const talentResult = await talentLoginWithGoogle(response.credential);

        if (talentResult.success) {
          setMessage({
            type: "success",
            text: "Google login successful! Redirecting to talent dashboard...",
          });
          navigate("/talent/dashboard");
          return;
        }

        // If talent login fails, try customer login
        console.log("Google OAuth: Attempting customer login...");
        const customerResult = await customerLoginWithGoogle(
          response.credential,
        );

        if (customerResult.success) {
          setMessage({
            type: "success",
            text: "Google login successful! Redirecting to customer dashboard...",
          });
          navigate("/customer/dashboard");
          return;
        }

        // If both fail
        setMessage({
          type: "error",
          text: "Google login failed. Account not found or login error.",
        });
      } catch (error) {
        console.error("Google OAuth: Callback error:", error);
        setMessage({
          type: "error",
          text: "Google login failed. Please try again.",
        });
      } finally {
        setIsGoogleLoading(false);
      }
    },
    [talentLoginWithGoogle, customerLoginWithGoogle, navigate],
  );

  // Initialize Google OAuth
  useEffect(() => {
    let isInitialized = false;

    const initializeGoogleOAuth = () => {
      if (isInitialized) return;

      if (window.google?.accounts?.id) {
        console.log("Google OAuth: Google accounts API available");
        try {
          window.google.accounts.id.initialize({
            client_id:
              process.env.REACT_APP_GOOGLE_CLIENT_ID ||
              "877295110750-se0qnm049lgsn63pga699j5npljnc6oq.apps.googleusercontent.com",
            callback: handleGoogleCallback,
            auto_select: false,
            cancel_on_tap_outside: true,
          });
          isInitialized = true;
          console.log("Google OAuth: Initialization complete");

          // Render the Google button
          if (googleButtonRef.current) {
            window.google.accounts.id.renderButton(googleButtonRef.current, {
              type: "standard",
              theme: "outline",
              size: "large",
              text: "continue_with",
              shape: "rectangular",
              logo_alignment: "left",
              width: googleButtonRef.current.offsetWidth || 300,
            });
            console.log("Google OAuth: Button rendered");
          }
        } catch (error) {
          console.error("Google OAuth: Initialization error:", error);
        }
      } else {
        console.error("Google OAuth: Google accounts API not available");
      }
    };

    // Check if script already exists
    const existingScript = document.querySelector(
      'script[src="https://accounts.google.com/gsi/client"]',
    );

    if (existingScript) {
      // Script already loaded
      initializeGoogleOAuth();
    } else {
      // Load Google OAuth script
      console.log("Google OAuth: Loading script...");
      const script = document.createElement("script");
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.defer = true;
      script.onload = () => {
        console.log("Google OAuth: Script loaded successfully");
        initializeGoogleOAuth();
      };
      script.onerror = (error) => {
        console.error("Google OAuth: Script failed to load:", error);
      };
      document.head.appendChild(script);
    }

    // Don't remove the script on unmount to prevent abort errors
    return () => {
      // Cleanup: cancel any ongoing prompts
      if (window.google?.accounts?.id) {
        try {
          window.google.accounts.id.cancel();
        } catch (error) {
          console.error("Google OAuth: Cancel error:", error);
        }
      }
    };
  }, [handleGoogleCallback]);

  // Handle Google sign-in button click
  const handleGoogleSignIn = () => {
    if (!window.google?.accounts?.id) {
      setMessage({
        type: "error",
        text: "Google Sign-In is not available. Please try again later.",
      });
      return;
    }

    try {
      // Use prompt with notification instead of direct call
      window.google.accounts.id.prompt((notification: any) => {
        if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
          console.log(
            "Google OAuth: Prompt not displayed:",
            notification.getNotDisplayedReason(),
          );
          // Fallback: show a message to user
          setMessage({
            type: "error",
            text: "Please enable popups for this site or try signing in manually.",
          });
        }
      });
    } catch (error) {
      console.error("Google OAuth: Prompt error:", error);
      setMessage({
        type: "error",
        text: "Failed to open Google Sign-In. Please try again.",
      });
    }
  };

  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear field error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission with automatic user type detection
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);
    setMessage({ type: "", text: "" });

    try {
      // Try talent login first
      const talentResult = await talentLogin(formData.email, formData.password);

      if (talentResult.success) {
        setMessage({
          type: "success",
          text: "Login successful! Redirecting to talent dashboard...",
        });
        navigate("/talent/dashboard");
        return;
      }

      // If talent login fails, try customer login
      const customerResult = await customerLogin(
        formData.email,
        formData.password,
      );

      if (customerResult.success) {
        setMessage({
          type: "success",
          text: "Login successful! Redirecting to customer dashboard...",
        });
        navigate("/customer/dashboard");
        return;
      }

      // If both fail, show generic error
      setMessage({
        type: "error",
        text: "Invalid email or password. Please check your credentials and try again.",
      });
    } catch (error) {
      setMessage({
        type: "error",
        text: "An unexpected error occurred. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Header />
      <div className="partner-login new-talent-css">
        <div className="partner-login-box">
          <div style={{ marginBottom: "30px", textAlign: "center" }}>
            <h3 style={{ marginBottom: "20px" }}>Sign In</h3>
            <p style={{ color: "#666", fontSize: "14px" }}>
              Sign in to your Skillseek account
            </p>
          </div>

          {/* <p>I agree to Skillseek <span className="link-placeholder">Terms of Service</span> and acknowledge Skillseek <span className="link-placeholder">Privacy Policy</span>.</p> */}

          {message.text && (
            <div
              className={`alert ${message.type === "success" ? "alert-success" : "alert-error"}`}
            >
              {message.text}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="email" className="form-label">
                Email Address
              </label>
              <div className="input-wrapper">
                <div className="input-icon">
                  <Mail size={18} />
                </div>
                <input
                  type="email"
                  className={`form-control ${errors.email ? "error" : ""}`}
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter your email"
                />
              </div>
              {errors.email && (
                <div className="error-message">{errors.email}</div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="password" className="form-label">
                Password
              </label>
              <div className="input-wrapper">
                <div className="input-icon">
                  <Lock size={18} />
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  className={`form-control ${errors.password ? "error" : ""}`}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {errors.password && (
                <div className="error-message">{errors.password}</div>
              )}
            </div>

            <div className="form-options">
              <label className="remember-me">
                <input type="checkbox" />
                <span>Remember me</span>
              </label>
              <Link to="/talent/forgot-password">Forgot password?</Link>
            </div>

            <button
              type="submit"
              className="login-button"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <div className="button-spinner"></div>
                  <span>Signing in...</span>
                </>
              ) : (
                "Sign in"
              )}
            </button>
          </form>

          <div className="continue-google">
            <button
              type="button"
              onClick={handleGoogleSignIn}
              disabled={isGoogleLoading}
              className={`google-signin-btn ${isGoogleLoading ? "loading" : ""}`}
            >
              {isGoogleLoading ? (
                <>
                  <div className="button-spinner"></div>
                  <span>Signing in with Google...</span>
                </>
              ) : (
                <>
                  <svg
                    version="1.1"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 48 48"
                    className="LgbsSe-Bz112c"
                  >
                    <g>
                      <path
                        fill="#EA4335"
                        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
                      ></path>
                      <path
                        fill="#4285F4"
                        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
                      ></path>
                      <path
                        fill="#FBBC05"
                        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
                      ></path>
                      <path
                        fill="#34A853"
                        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
                      ></path>
                      <path fill="none" d="M0 0h48v48H0z"></path>
                    </g>
                  </svg>
                  Continue with Google
                </>
              )}
            </button>
            <p className="google-note">
              Google Sign-In works for both Service Provider and Customer
              accounts.
            </p>
          </div>

          <div className="login-footer">
            <p>
              Don't have an account?
              <Link to="/register"> Create Account</Link>
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default TalentLogin;
